'use client';

import { useEffect, useRef, type ReactNode } from 'react';

interface RevealProps {
	children: ReactNode;
	/** Retardo en ms, para escalonar hermanos de una misma fila. */
	delay?: number;
	className?: string;
}

/**
 * Aparición al hacer scroll.
 *
 * MEJORA PROGRESIVA, deliberadamente: el servidor pinta el contenido SIN clases
 * de animación, o sea visible. Solo después de montar —y solo si quedó fuera de
 * la pantalla— se le agrega la clase que lo oculta para animarlo. De ahí salen
 * las tres propiedades que importan:
 *
 *  1. Sin JS (o si el observer falla) no se esconde nada: el HTML servido es la
 *     página completa, que es lo que exige la regla de SEO del proyecto. Un
 *     reveal ingenuo renderiza con `opacity: 0` y le entrega a Google una
 *     página en blanco.
 *  2. Lo que ya está en pantalla al cargar nunca parpadea: no se oculta para
 *     volver a aparecer.
 *  3. `prefers-reduced-motion` corta el efecto antes de tocar el DOM.
 *
 * Envuelve el CONTENIDO de cada banda, no la banda entera: el bloque de la
 * portada recorta con `overflow: hidden`, así que mover una banda completa
 * dejaría ver el fondo blanco del bloque por detrás.
 *
 * Las clases se aplican por `classList` y no por estado de React porque es un
 * efecto puramente visual que no cambia el árbol renderizado; pasarlo por
 * `setState` costaría un render extra por bloque (y la regla
 * `react-hooks/set-state-in-effect` del repo lo prohíbe).
 */
export function Reveal({ children, delay = 0, className }: RevealProps) {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const node = ref.current;
		if (!node) return;
		if (typeof IntersectionObserver === 'undefined') return;
		if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
		// Ya visible al cargar → se deja quieto.
		if (node.getBoundingClientRect().top < window.innerHeight * 0.92) return;

		node.classList.add('siiv-reveal');
		if (delay) node.style.transitionDelay = `${delay}ms`;

		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					if (!entry.isIntersecting) continue;
					node.classList.add('siiv-reveal-visible');
					observer.disconnect();
				}
			},
			{ rootMargin: '0px 0px -10% 0px' },
		);

		observer.observe(node);
		return () => observer.disconnect();
	}, [delay]);

	return (
		<div ref={ref} className={className}>
			{children}
		</div>
	);
}
